import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  LayoutGrid,
  List,
  X,
  Shield,
  ArrowRight,
  Sparkles,
  Pencil,
  FileOutput,
  Combine,
  Shrink,
  Lock,
  ScanText,
  Brain,
} from "lucide-react";
import { ToolCard } from "@/components/dashboard/ToolCard";
import { InternalLinkSection } from "@/components/seo/InternalLinkSection";
import { useSEO } from "@/hooks/useSEO";
import { useOnboardingTooltip } from "@/hooks/useOnboardingTooltip";
import { TOOL_DEFINITIONS } from "@/config/tools";
import { TOOL_SEO_ROUTES } from "@/config/toolSeoRegistry";
import { buildBreadcrumbSchema, buildCollectionPageSchema } from "@/lib/seo/schema";

// Define category order and icons
const CATEGORIES = [
  {
    key: "edit",
    label: "Edit & Modify",
    icon: Pencil,
    description: "Edit, sign, annotate, and modify PDF content",
  },
  {
    key: "convert",
    label: "Convert",
    icon: FileOutput,
    description: "Transform PDFs to other formats and vice versa",
  },
  {
    key: "combine",
    label: "Organize & Merge",
    icon: Combine,
    description: "Merge, split, and batch process PDFs",
  },
  {
    key: "file-size",
    label: "Optimize",
    icon: Shrink,
    description: "Compress and reduce file size",
  },
  {
    key: "security",
    label: "Security & Privacy",
    icon: Lock,
    description: "Encrypt, decrypt, and protect your PDFs",
  },
  {
    key: "export",
    label: "Extract & Analyze",
    icon: ScanText,
    description: "Extract data, OCR, and inspect PDFs",
  },
  {
    key: "ai",
    label: "AI Tools",
    icon: Brain,
    description: "Chat, summarize, quiz, and analyze PDFs with AI",
  },
];

// Map tools to their display categories
const categoryMapping: Record<string, string> = {
  // AI Tools
  "chat-with-pdf": "ai",
  "pdf-quiz": "ai",
  "pdf-timeline": "ai",
  "pdf-summarize": "ai",
  "pdf-keypoints": "ai",
  "pdf-flashcards": "ai",
  "pdf-studyguide": "ai",
  // Edit
  "edit-pdf-visual": "edit",
  "esign-pdf": "edit",
  "crop-pdf": "edit",
  "remove-pages": "edit",
  "add-page-numbers": "edit",
  "watermark-pdf": "edit",
  "redact-text": "edit",
  "edit-pdf-layout": "edit",
  "header-footer": "edit",
  // Convert
  "pdf-to-image": "convert",
  "pdf-to-word": "convert",
  "pdf-to-html": "convert",
  "pdf-to-webp": "convert",
  "pdf-to-epub": "convert",
  "extract-pages": "convert",
  "pdf-to-zip": "convert",
  "image-to-pdf": "convert",
  "webp-to-pdf": "convert",
  "svg-to-pdf": "convert",
  "epub-to-pdf": "convert",
  "word-to-pdf": "convert",
  "markdown-to-pdf": "convert",
  // Combine
  "merge-pdf": "combine",
  "split-pdf": "combine",
  "mail-merge": "combine",
  "batch-stamp": "combine",
  // File size
  "compress-pdf": "file-size",
  compress: "file-size",
  "increase-pdf-size": "file-size",
  "enhance-pdf": "edit",
  "flatten-pdf": "edit",
  // Security
  "encrypt-pdf": "security",
  "decrypt-pdf": "security",
  // Export
  "extract-form-data": "export",
  "ocr-pdf": "export",
  "extract-text-ocr": "export",
  "pdf-inspector": "export",
  "create-pdf": "convert",
  "invoice-generator": "export",
  "qrcode-generator": "export",
  "csv-to-pdf": "export",
  "extract-images": "export",
  "split-by-size": "split",
  "form-builder": "edit",
  "pdf-diff": "export",
  "pdf-to-excel": "convert",
  "sign-fill": "edit",
  "pdf-translator": "export",
  "pdf-to-pptx": "convert",
  "excel-to-pdf": "convert",
  "html-to-pdf": "convert",
  "pdf-repair": "cleanup",
  "page-extractor": "split",
  "add-bookmarks": "edit",
  "pdf-metadata": "edit",
  "pdf-permissions": "security",
  "pdf-security-audit": "security",
  "pdf-sanitizer": "security",
  "deep-metadata-explorer": "security",
};

const featuredToolLinks = TOOL_SEO_ROUTES.filter((route) =>
  [
    "/tools/merge",
    "/tools/compress",
    "/tools/split",
    "/tools/remove-pages",
    "/tools/edit-pdf",
    "/tools/pdf-to-excel",
    "/tools/pdf-translator",
    "/tools/pdf-security-audit",
    "/tools/ai/chat",
  ].includes(route.path),
).map((route) => ({
  href: route.path,
  label: route.name,
  description: route.description,
}));

const convertToolLinks = TOOL_SEO_ROUTES.filter((route) =>
  [
    "/tools/convert/pdf-to-word",
    "/tools/word-to-pdf",
    "/tools/pdf-to-excel",
    "/tools/pdf-to-pptx",
    "/tools/html-to-pdf",
    "/tools/excel-to-pdf",
  ].includes(route.path),
).map((route) => ({
  href: route.path,
  label: route.name,
  description: route.description,
}));

const secureToolLinks = TOOL_SEO_ROUTES.filter((route) =>
  [
    "/tools/encrypt",
    "/tools/decrypt",
    "/tools/pdf-security-audit",
    "/tools/pdf-sanitizer",
    "/tools/pdf-permissions",
    "/tools/pdf-signer",
  ].includes(route.path),
).map((route) => ({
  href: route.path,
  label: route.name,
  description: route.description,
}));

const editToolLinks = TOOL_SEO_ROUTES.filter((route) =>
  [
    "/tools/edit-pdf",
    "/tools/edit-pdf-layout",
    "/tools/rearrange",
    "/tools/remove-pages",
    "/tools/crop",
    "/tools/header-footer",
  ].includes(route.path),
).map((route) => ({
  href: route.path,
  label: route.name,
  description: route.description,
}));

const aiToolLinks = TOOL_SEO_ROUTES.filter((route) => route.family === "ai").map(
  (route) => ({
    href: route.path,
    label: route.name,
    description: route.description,
  }),
);

const Tools = () => {
  useSEO({
    title: "All PDF Tools | Browser-Based PDF Utilities | PDFWorkSpace",
    description:
      "Browse PDF tools for merging, splitting, compressing, converting, editing, and securing documents with browser-based workflows.",
    keywords:
      "all pdf tools, browser pdf tools, merge split compress edit pdf, no upload pdf tools",
    canonicalPath: "/tools",
    jsonLd: [
      buildCollectionPageSchema({
        title: "All PDF Tools",
        path: "/tools",
        description:
          "Collection page for browser-based PDF editing, conversion, security, and organization tools.",
      }),
      buildBreadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "All PDF Tools", path: "/tools" },
      ]),
    ],
  });

  const { visible, dismiss } = useOnboardingTooltip();
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [query, setQuery] = useState("");

  // Group tools by category
  const toolsByCategory = useMemo(() => {
    const q = query.toLowerCase().trim();
    const grouped: Record<string, typeof TOOL_DEFINITIONS> = {};

    // Initialize all categories
    CATEGORIES.forEach((cat) => {
      grouped[cat.key] = [];
    });

    // Assign tools to categories
    TOOL_DEFINITIONS.forEach((tool) => {
      const catKey = categoryMapping[tool.id] || tool.categoryKey;

      // Filter by search query
      if (q) {
        const haystack =
          `${tool.name} ${tool.description} ${tool.categoryLabel}`.toLowerCase();
        if (!haystack.includes(q)) return;
      }

      if (grouped[catKey]) {
        grouped[catKey].push(tool);
      } else {
        // Fallback to first matching category
        const fallbackCat = CATEGORIES.find((c) => c.key === tool.categoryKey);
        if (fallbackCat) {
          grouped[fallbackCat.key].push(tool);
        }
      }
    });

    return grouped;
  }, [query]);

  const totalCount = TOOL_DEFINITIONS.length;
  const filteredCount = Object.values(toolsByCategory).reduce(
    (acc, tools) => acc + tools.length,
    0,
  );

  return (
    <div className="bg-background min-h-screen">
      <section className="mx-auto max-w-6xl px-4 py-8 md:py-10">
        {/* Onboarding tooltip */}
        {visible && (
          <div className="mb-8 rounded-xl border border-border bg-card p-5 shadow-sm opacity-0 animate-fade-in">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-secondary p-2.5">
                  <Sparkles className="h-5 w-5 text-foreground" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    Welcome to PDFWorkSpace Tools
                  </p>
                  <p className="text-sm text-text-secondary max-w-lg">
                    All tools run locally in this browser tab. Just pick a tool,
                    drop your files, and download the result. Your PDFs are
                    never uploaded to any server.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="shrink-0 rounded-full p-1.5 text-text-secondary transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">All Tools</h1>
          <p className="text-text-secondary">
            {totalCount} tools to edit, convert, and manage your PDFs — all
            running locally
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-text-secondary/50" />
            <input
              id="tools-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search tools... (e.g. merge, compress, watermark)"
              className="h-14 w-full rounded-2xl border border-border bg-card pl-14 pr-14 text-md text-foreground shadow-sm outline-none transition-all placeholder:text-text-secondary/50 focus:border-foreground/30 focus:shadow-md focus:ring-4 focus:ring-foreground/5"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-5 top-1/2 -translate-y-1/2 rounded-full bg-secondary p-1.5 text-text-secondary transition-colors hover:bg-foreground hover:text-background"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Results count and view toggle */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-text-secondary">
              {query ? (
                <>
                  <strong className="text-foreground">{filteredCount}</strong>{" "}
                  {filteredCount === 1 ? "tool" : "tools"} found
                </>
              ) : null}
            </span>
            <div className="flex items-center rounded-xl border border-border bg-card p-1">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`rounded-lg p-2.5 transition-all ${
                  viewMode === "grid"
                    ? "bg-foreground text-background shadow-sm"
                    : "text-text-secondary hover:text-foreground"
                }`}
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`rounded-lg p-2.5 transition-all ${
                  viewMode === "list"
                    ? "bg-foreground text-background shadow-sm"
                    : "text-text-secondary hover:text-foreground"
                }`}
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Category Sections */}
        {filteredCount === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border bg-card/50 p-12 text-center opacity-0 animate-fade-in">
            <div className="mx-auto max-w-md space-y-4">
              <div className="mx-auto w-fit rounded-full bg-secondary p-4">
                <Search className="h-8 w-8 text-text-secondary" />
              </div>
              <p className="text-xl font-semibold text-foreground">
                No tools match your search
              </p>
              <p className="text-sm text-text-secondary">
                Try clearing the search or using different keywords.
              </p>
              <button
                type="button"
                onClick={() => setQuery("")}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background transition-all hover:bg-foreground/90"
              >
                Clear search
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-10">
            {CATEGORIES.map((category) => {
              const tools = toolsByCategory[category.key] || [];
              if (tools.length === 0) return null;

              return (
                <section
                  key={category.key}
                  className="opacity-0 animate-fade-in"
                >
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary">
                      <category.icon className="h-5 w-5 text-foreground" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">
                        {category.label}
                      </h2>
                      <p className="text-sm text-text-secondary">
                        {category.description}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <span className="text-xs font-medium text-text-secondary bg-secondary px-2.5 py-1 rounded-full">
                        {tools.length} {tools.length === 1 ? "tool" : "tools"}
                      </span>
                    </div>
                  </div>

                  {/* Tools Grid/List */}
                  <div
                    className={
                      viewMode === "grid"
                        ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                        : "flex flex-col gap-3"
                    }
                  >
                    {tools.map((tool, index) => (
                      <div
                        key={tool.id}
                        className="opacity-0 animate-fade-in-up"
                        style={{
                          animationDelay: `${0.03 * Math.min(index, 8)}s`,
                        }}
                      >
                        <ToolCard
                          name={tool.name}
                          description={tool.description}
                          href={tool.href}
                          category={tool.categoryLabel}
                          badge={tool.badge}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 space-y-6">
          <InternalLinkSection
            title="Featured PDF Workflows"
            description="These are the highest-intent single-file routes and they anchor the main discovery path from the tools hub."
            links={featuredToolLinks}
          />
          <InternalLinkSection
            title="Convert PDF and Office Files"
            description="Conversion searches tend to be very route-specific, so these pages are grouped together for stronger topical crawling."
            links={convertToolLinks}
            compact
          />
          <InternalLinkSection
            title="Edit and Prepare PDF Documents"
            description="These routes cover page cleanup, visual editing, layout changes, and output preparation."
            links={editToolLinks}
            compact
          />
          <InternalLinkSection
            title="Secure and Review PDF Files"
            description="These routes focus on permissions, encryption, sanitization, signatures, and document security review."
            links={secureToolLinks}
            compact
          />
          <InternalLinkSection
            title="AI PDF Tool Pages"
            description="AI routes are grouped here so study and analysis pages stay connected to the main tools hub."
            links={aiToolLinks}
            compact
          />
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-secondary/50 to-secondary/20 p-8 text-center md:p-10">
          <div className="mx-auto max-w-lg space-y-4">
            <div className="mx-auto w-fit rounded-full bg-foreground/10 p-3">
              <Shield className="h-6 w-6 text-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">
              Your privacy comes first
            </h3>
            <p className="text-sm text-text-secondary">
              All {totalCount} tools work entirely in your browser. No files are
              ever uploaded, and we have zero access to your documents.
            </p>
            <Link
              to="/about-privacy"
              className="group mt-4 inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline"
            >
              Learn about our privacy commitment
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tools;
